import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useParams } from 'react-router-dom';
import { bulkCreateInvitationSchema, BulkCreateInvitationFormValues } from '@/lib/validations/invitationSchema';
import { useInvitationStore } from '@/lib/store/invitationStore';
import { Alert, Button, Select, Card } from '@/components/common';
import { Textarea } from '@/components/common/Textarea';
import { ShiftTypeSelector } from './ShiftTypeSelector';

const roleOptions = [
  { value: 'provider', label: 'Provider' },
  { value: 'admin', label: 'Administrator' }
];

export default function BulkInviteForm() {
  const { teamId } = useParams<{ teamId: string }>();
  const { bulkCreateInvitations } = useInvitationStore();
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [shiftType, setShiftType] = useState<'days_only' | 'nights_only' | 'both'>('both');
  
  const { register, handleSubmit, reset, setValue, formState: { errors } } = useForm<BulkCreateInvitationFormValues>({
    resolver: zodResolver(bulkCreateInvitationSchema),
    defaultValues: {
      emails: '',
      role: 'provider',
      shift_type: 'both'
    }
  });
  
  const onSubmit = async (data: BulkCreateInvitationFormValues) => {
    if (!teamId) return;
    
    setIsSubmitting(true);
    setError(null);
    setSuccess(null);
    
    // Parse emails, removing empty entries
    const emails = data.emails.split(/[\s,;]+/).filter(Boolean);
    
    try {
      await bulkCreateInvitations({
        team_id: teamId,
        invitations: emails.map(email => ({
          email,
          role: data.role,
          shift_type: data.shift_type
        }))
      });
      
      setSuccess(`Invitations sent to ${emails.length} recipients`);
      reset();
      setShiftType('both');
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess(null);
      }, 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send invitations. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <Card className="p-6">
      <h2 className="text-xl font-semibold mb-4">Bulk Invite Users</h2>
      
      {error && (
        <Alert variant="error" className="mb-4">
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert variant="success" className="mb-4">
          {success}
        </Alert>
      )}
      
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Textarea
          label="Email Addresses"
          {...register('emails')}
          error={errors.emails?.message}
          placeholder="Enter multiple email addresses separated by commas, spaces, or new lines"
          rows={5}
          required
        />
        
        <Select
          label="Role"
          options={roleOptions}
          {...register('role')}
          error={errors.role?.message}
          required
        />
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Shift Type
          </label>
          <ShiftTypeSelector
            value={shiftType}
            onChange={(value) => {
              setShiftType(value);
              setValue('shift_type', value);
            }}
            disabled={isSubmitting}
          />
          {errors.shift_type?.message && (
            <p className="text-red-500 text-xs mt-1">{errors.shift_type.message}</p>
          )}
        </div>
        
        <div className="pt-2">
          <Button
            type="submit"
            variant="primary"
            isLoading={isSubmitting}
            className="w-full"
          >
            Send Invitations
          </Button>
        </div>
      </form>
    </Card>
  );
} 